// Centralised permission/access lookups. Extracted from the old Layout.js
// so the new AppShell + the testing agent can both read from a single source.
//
// permKey → user.permissions[key] = {view, edit}
// `null` = always-accessible (Dashboard, Profile, Support).
// `undefined` for an unmapped path resolves to "always allow" (safer default
// for new routes — gate them explicitly when permissions are needed).
//
// Permission keys mirror the 7 dock pillars + Settings + Alerts, so what a
// tenant owner sees in the user-management UI = exactly what end users see
// in the bottom dock and side drawer. Legacy fine-grained keys
// (master_styles, rate_master, demand, lots, ledgers, invoices, contractors)
// are mapped to their parent pillar below.

export const PERM_MAP = {
  '/dashboard': null,
  // Sampling
  '/sampling': 'sampling',
  // Costing (includes master styles + all costing pages)
  '/master-styles': 'costing',
  '/cost-master': 'costing',
  '/costings': 'costing',
  // Materials
  '/materials': 'materials',
  // Purchase Orders
  '/purchase-orders': 'purchase_orders',
  '/po-completion': 'purchase_orders',
  // Cutting (covers cutting planner, demand dashboard, analysis sub-pages)
  '/cutting': 'cutting',
  '/cutting/pick-style': 'cutting',
  '/cutting/tables': 'cutting',
  '/cutting/done': 'cutting',
  '/cutting/analysis': 'cutting',
  '/demand': 'cutting',
  // Production (covers lots & tracking, production status, contractors, rate master)
  '/lots': 'production',
  '/production': 'production',
  '/contractors': 'production',
  '/rate-master': 'production',
  // Packing (new pillar)
  '/packing': 'packing',
  // Finance (invoices + ledgers)
  '/invoices': 'finance',
  '/ledgers': 'finance',
  '/billing': 'finance',
  // Alerts (cross-cutting)
  '/alerts': 'alerts',
  // Settings
  '/settings': 'settings',
  // Always-accessible utility routes
  '/super-admin': null,
  '/support': null,
  '/training': null,
  '/profile': null,
};

// Legacy permission keys that we've consolidated under a parent dock pillar.
// `hasViewAccess` / `hasEditAccess` read from both the new and legacy keys so
// a tenant can migrate users at their own pace (or never) without anyone
// losing access on the day of the rename. New keys always take precedence.
const LEGACY_PARENTS = {
  master_styles: 'costing',
  rate_master:   'production',
  contractors:   'production',
  lots:          'production',
  demand:        'cutting',
  invoices:      'finance',
  ledgers:       'finance',
};

function getEffectivePerm(user, permKey) {
  const perms = user?.permissions || {};
  let view = false, edit = false;
  if (perms[permKey]) {
    view = view || !!perms[permKey].view;
    edit = edit || !!perms[permKey].edit;
  }
  // OR-merge any legacy keys pointing at this parent pillar
  Object.entries(LEGACY_PARENTS).forEach(([legacy, parent]) => {
    if (parent === permKey && perms[legacy]) {
      view = view || !!perms[legacy].view;
      edit = edit || !!perms[legacy].edit;
    }
  });
  return { view, edit };
}

export function hasViewAccess(user, path) {
  if (!user) return false;
  const role = user.role;
  if (path === '/super-admin') return role === 'super_admin';
  // iter164d — Strip query string for perm lookup so nav items pointing
  // to the same page with a different ?tab=... share the same permission.
  const cleanPath = String(path || '').split('?')[0];
  const permKey = PERM_MAP[cleanPath];
  if (permKey === null || permKey === undefined) return true;
  if (role === 'owner' || role === 'super_admin') return true;
  return getEffectivePerm(user, permKey).view;
}

export function hasEditAccess(user, permKey) {
  if (!user) return false;
  if (user.role === 'owner' || user.role === 'super_admin') return true;
  return getEffectivePerm(user, permKey).edit;
}
